// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import type { CognitoOAuthConfig } from "./auth/CognitoAuthProvider";

// ── Inject Content Security Policy from build-time env vars ─────────────────
const apiUrl = import.meta.env.VITE_API_URL ?? "";
const cognitoLoginUrl = import.meta.env.VITE_COGNITO_LOGIN_URL ?? "";
const localAgentUrl = import.meta.env.VITE_LOCAL_AGENT_URL ?? "http://127.0.0.1:8880";

// Extract origins for CSP connect-src
const connectSources = [
  "'self'",
  apiUrl ? new URL(apiUrl).origin : "",
  cognitoLoginUrl ? new URL(cognitoLoginUrl).origin : "",
  localAgentUrl ? new URL(localAgentUrl).origin : "",
  // Also allow the Cognito token endpoint (same origin as login URL)
].filter(Boolean);

// Deduplicate
const uniqueConnectSources = [...new Set(connectSources)].join(" ");

const cspContent = [
  "default-src 'self'",
  "script-src 'self'",
  `connect-src ${uniqueConnectSources}`,
  "style-src 'self' 'unsafe-inline'",
  "img-src 'self' data:",
  "font-src 'self'",
  "frame-src 'none'",
  "object-src 'none'",
  "base-uri 'self'",
  `form-action 'self' ${cognitoLoginUrl ? new URL(cognitoLoginUrl).origin : ""}`.trim(),
].join("; ");

const cspMeta = document.createElement("meta");
cspMeta.httpEquiv = "Content-Security-Policy";
cspMeta.content = cspContent;
document.head.appendChild(cspMeta);

// ── OAuth config ────────────────────────────────────────────────────────────
const loginUrl = import.meta.env.VITE_COGNITO_LOGIN_URL ?? "";
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID ?? "";
const redirectUri = import.meta.env.VITE_COGNITO_REDIRECT_URI ?? "";

const oauthConfig: CognitoOAuthConfig = {
  loginUrl,
  logoutUrl: import.meta.env.VITE_COGNITO_LOGOUT_URL ?? "",
  clientId,
  redirectUri,
  tokenEndpoint: `${loginUrl}/oauth2/token`,
};

const root = document.getElementById("root");
if (root) {
  createRoot(root).render(
    <React.StrictMode>
      <App oauthConfig={oauthConfig} />
    </React.StrictMode>,
  );
}
